/**
 * combat.ts — the combat maths, shared so the server, the tests and the client's
 * debug views all agree by construction (AGENTS.md: server-authoritative).
 *
 * The centrepiece is `swordSequence`: THE function that produces the sword's
 * world-space transform sequence for a tick. Both the sweep shape-casts and the
 * contact-velocity computation consume its output — never separate sources. If
 * those two ever diverge, a graze does full damage and camera drag stops
 * mattering, so the invariant is enforced by construction here and asserted in
 * `combat.test.ts`.
 *
 * Nothing in this file touches Rapier, the ECS or the network: it is pure
 * geometry and arithmetic, which is what makes it testable.
 */
import { FEEL, isEmoteId } from './feel.js';
import { wrapAngle } from './protocol.js';
import { armorAt } from './armor.js';
import { rotateByQuat, readSample, sampleCount, swordCapsuleLocal, } from './trajectory.js';
import { TRAJECTORIES } from './generated/trajectories.js';
/** Where an attack is, given how long it has been running. */
export function attackPhase(clipId, elapsedMs) {
    const { windupMs, activeMs, recoveryMs } = FEEL.CLIPS[clipId];
    if (elapsedMs < windupMs)
        return 'windup';
    if (elapsedMs < windupMs + activeMs)
        return 'active';
    if (elapsedMs < windupMs + activeMs + recoveryMs)
        return 'recovery';
    return 'done';
}
/**
 * Whether this clip may skip recovery into a follow-up (Mordhau combo).
 *
 * Stabs never combo: skipping recovery let them be spammed back-to-back. A
 * thrust can still be queued in the buffer window, but it only fires after
 * recovery has fully elapsed.
 */
export function canCombo(clipId) {
    return clipId === 'slash';
}
/**
 * True while an input arriving now should be queued as a follow-up.
 *
 * For slash→slash the follow-up fires as a combo (skips recovery). For stabs
 * the same window only queues — see `canCombo`.
 */
export function inBufferWindow(clipId, elapsedMs) {
    const { windupMs, activeMs, recoveryMs } = FEEL.CLIPS[clipId];
    const openAt = windupMs + activeMs - FEEL.COMBO_EARLY_MS;
    const total = windupMs + activeMs + recoveryMs;
    return elapsedMs >= openAt && elapsedMs < total;
}
/**
 * Clip time a combo follow-up begins at — the wound-back park.
 *
 * Fresh swings still start at 0. Combos never visit rest; the blend carries the
 * blade from the previous cut's end into this pose.
 */
export function chainEntryMs(clipId, _dialDelta = 0) {
    return FEEL.CLIPS[clipId].windupMs * FEEL.CHAIN_ENTRY_AT;
}
/**
 * Rate-limits a dial angle toward a target. Shortest path, never past `maxStep`.
 *
 * Used by directional chains so the blade rolls from the previous cut's plane
 * into the next instead of teleporting onto the new dial.
 */
export function slewDialAngle(current, target, maxStep) {
    if (maxStep <= 0)
        return wrapAngle(current);
    const delta = wrapAngle(target - current);
    if (Math.abs(delta) <= maxStep)
        return wrapAngle(target);
    return wrapAngle(current + Math.sign(delta) * maxStep);
}
/** Normalized clip time for the snapshot's animState. */
export function clipProgress(clipId, elapsedMs) {
    const { windupMs, activeMs, recoveryMs } = FEEL.CLIPS[clipId];
    const total = windupMs + activeMs + recoveryMs;
    return total > 0 ? Math.min(1, Math.max(0, elapsedMs / total)) : 0;
}
// ---------------------------------------------------------------------------
// trajectory sampling
// ---------------------------------------------------------------------------
const lerp = (a, b, t) => a + (b - a) * t;
/** Shortest-path quaternion interpolation. */
export function slerp(a, b, t) {
    let [bx, by, bz, bw] = b;
    let dot = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
    // Quaternions double-cover rotations: flip to take the short way round.
    if (dot < 0) {
        bx = -bx;
        by = -by;
        bz = -bz;
        bw = -bw;
        dot = -dot;
    }
    if (dot > 0.9995) {
        // Nearly parallel — lerp and renormalize, which avoids a division blow-up.
        const out = [
            lerp(a[0], bx, t),
            lerp(a[1], by, t),
            lerp(a[2], bz, t),
            lerp(a[3], bw, t),
        ];
        const len = Math.hypot(out[0], out[1], out[2], out[3]) || 1;
        return [out[0] / len, out[1] / len, out[2] / len, out[3] / len];
    }
    const theta = Math.acos(dot);
    const sinTheta = Math.sin(theta);
    const wa = Math.sin((1 - t) * theta) / sinTheta;
    const wb = Math.sin(t * theta) / sinTheta;
    return [a[0] * wa + bx * wb, a[1] * wa + by * wb, a[2] * wa + bz * wb, a[3] * wa + bw * wb];
}
/**
 * The baked sample at an arbitrary clip time, interpolated between the two
 * neighbouring 60Hz samples. Times outside the baked window clamp to its ends.
 */
export function sampleTrajectoryAt(traj, clipTimeMs) {
    const count = sampleCount(traj);
    const stepMs = 1000 / traj.hz;
    const raw = (clipTimeMs - traj.startMs) / stepMs;
    if (raw <= 0)
        return readSample(traj, 0);
    if (raw >= count - 1)
        return readSample(traj, count - 1);
    const i = Math.floor(raw);
    const t = raw - i;
    const a = readSample(traj, i);
    const b = readSample(traj, i + 1);
    return {
        pos: [lerp(a.pos[0], b.pos[0], t), lerp(a.pos[1], b.pos[1], t), lerp(a.pos[2], b.pos[2], t)],
        rot: slerp(a.rot, b.rot, t),
    };
}
// ---------------------------------------------------------------------------
// the dial: rolling the canonical arc to any radial direction
// ---------------------------------------------------------------------------
/**
 * The swing plane's normal for a given dial angle, in character-root space.
 *
 * The plane a cut travels in ALWAYS CONTAINS THE FORWARD AXIS — the direction
 * the opponent is in — and turns about it with the dial. Dial 0 is a level cut
 * and its plane is horizontal, so the normal is up; a dial of 90 degrees rolls
 * that into an overhead, whose plane is vertical.
 *
 * One fixed plane for every direction was the wrong idea and looked it: a cut
 * from the right and an overhead do not travel in the same plane, and forcing
 * them to made the sweep run in the wrong dimension entirely.
 */
export function swingNormal(dial = 0) {
    // Roll the level plane's normal (up) about the forward axis.
    return [-Math.sin(dial), Math.cos(dial), 0];
}
export function rollSample(sample, angle) {
    if (angle === 0)
        return sample;
    // About the FORWARD axis, through SLASH_PIVOT. The arc centre sits on that
    // axis, so rolling leaves the centre exactly where it is and only turns the
    // plane the cut travels in.
    const half = angle / 2;
    const q = [0, 0, Math.sin(half), Math.cos(half)];
    const [px, py, pz] = FEEL.SLASH_PIVOT;
    const local = rotateByQuat([sample.pos[0] - px, sample.pos[1] - py, sample.pos[2] - pz], q);
    const pos = [px + local[0], py + local[1], pz + local[2]];
    // Carry the blade's own orientation round with it: q * rot.
    const rot = mulQuat(q, sample.rot);
    return { pos, rot };
}
/** Wraps a dial angle into (-PI, PI]. */
export const normalizeDial = (angle) => wrapAngle(angle);
/** Straight down on the dial: the centre of the forbidden wedge. */
const DIAL_DOWN = -Math.PI / 2;
/**
 * Keeps the dial out of the dead arc at the bottom.
 *
 * A swing coming from directly below is a rising cut from between your own feet
 * — nobody fights like that and it reads badly. Aiming into the wedge snaps to
 * whichever edge is nearer, so the dial never sits somewhere you cannot throw
 * from and the HUD never shows a direction that will not happen.
 *
 * Applied on the client so the dial visibly refuses, and again on the server so
 * a hand-written packet cannot bypass it.
 */
/**
 * Which SIDE a dial angle throws from: false is the sword side, true is across.
 *
 * The boundary is straight up and straight down, because that is where
 * `localPoseAt` stops rolling the arc and starts mirroring it — so the same
 * predicate has to decide both, or a swing could be aimed at a side it is not
 * drawn on.
 */
export const isMirroredDial = (angle) => Math.abs(wrapAngle(angle)) > Math.PI / 2;
/**
 * Re-aims a dial angle without letting it cross to the other side.
 *
 * A wound-up swing is held out on one side of the body. Letting the dial carry
 * it across means the sword jumps the whole way over — through the man holding
 * it — because past the vertical the arc is MIRRORED rather than rolled further.
 * Within a side you may aim anywhere; to throw from the other one you have to
 * let this swing go, or feint out of it.
 *
 * The stop lands just inside the boundary, then goes back through the dead-arc
 * clamp, which is what keeps a swing pinned at the bottom out of the wedge
 * nobody swings from.
 */
export function clampToSwingSide(angle, mirrored) {
    const aimed = clampDialAngle(angle);
    if (isMirroredDial(aimed) === mirrored)
        return aimed;
    // Crossing. Stop at whichever end of this side is nearer to where they aimed.
    const up = Math.PI / 2;
    const edge = Math.abs(wrapAngle(aimed - up)) < Math.abs(wrapAngle(aimed + up)) ? up : -up;
    const inside = 1e-3 * Math.sign(edge);
    return clampDialAngle(mirrored ? edge + inside : edge - inside);
}
export function clampDialAngle(angle) {
    const a = wrapAngle(angle);
    const half = FEEL.DIAL_DEAD_ARC / 2;
    const delta = wrapAngle(a - DIAL_DOWN);
    if (Math.abs(delta) >= half)
        return a;
    return wrapAngle(DIAL_DOWN + (delta >= 0 ? half : -half));
}
/** Rotates a root-space point into world space for a given root transform. */
export function rootToWorld(root, local) {
    // Yaw about +Y: matches forwardVector(yaw) = (-sin, 0, -cos).
    const s = Math.sin(root.yaw);
    const c = Math.cos(root.yaw);
    const [x, y, z] = local;
    return {
        x: root.pos.x + x * c + z * s,
        y: root.pos.y + y,
        z: root.pos.z + -x * s + z * c,
    };
}
/** Interpolates a root transform, taking the short way around on yaw. */
export function lerpRoot(prev, curr, t) {
    return {
        pos: {
            x: lerp(prev.pos.x, curr.pos.x, t),
            y: lerp(prev.pos.y, curr.pos.y, t),
            z: lerp(prev.pos.z, curr.pos.z, t),
        },
        yaw: wrapAngle(prev.yaw + wrapAngle(curr.yaw - prev.yaw) * t),
    };
}
/**
 * THE sword transform sequence for one tick (AGENTS.md: one transform sequence).
 *
 * Sub-samples the portion of the active window this tick covers at
 * FEEL.SUBSAMPLE_HZ. Each baked local sample is rolled by the dial angle and
 * then composed with the attacker's root transform interpolated — position AND
 * yaw — to that instant:  world = root(t) × roll(angle) × baked(t).
 *
 * Turning into the
 * swing therefore adds blade speed and turning away removes it, with the tip
 * gaining more than the hilt, purely as a consequence of this composition.
 *
 * Returns an empty array if the tick does not overlap the active window, and
 * always at least two poses otherwise, because a sweep needs somewhere to go.
 */
export function swordSequence(params) {
    const { traj, angle, prevRoot, currRoot, tickStartMs, tickEndMs } = params;
    // The whole clip is baked; only this slice can hit anything.
    const windowStart = traj.activeStartMs;
    const windowEnd = traj.activeEndMs;
    const from = Math.max(tickStartMs, windowStart);
    const to = Math.min(tickEndMs, windowEnd);
    if (to <= from)
        return [];
    const tickSpanMs = tickEndMs - tickStartMs;
    const stepMs = 1000 / FEEL.SUBSAMPLE_HZ;
    const steps = Math.max(1, Math.ceil((to - from) / stepMs));
    const poses = [];
    for (let i = 0; i <= steps; i += 1) {
        const timeMs = from + ((to - from) * i) / steps;
        // Where this instant falls within the tick, for interpolating the root.
        const alpha = tickSpanMs > 0 ? (timeMs - tickStartMs) / tickSpanMs : 1;
        const root = lerpRoot(prevRoot, currRoot, Math.min(1, Math.max(0, alpha)));
        // THE pose function — the same call the client draws from. Two functions
        // over the same data is how a swing once ended up composed against the
        // capsule centre on one side and the feet on the other.
        const local = localPoseAt(traj, timeMs, angle);
        poses.push({
            timeMs,
            alpha,
            hilt: rootToWorld(root, local.hilt),
            tip: rootToWorld(root, local.tip),
            root,
        });
    }
    return poses;
}
/** A point along the blade: 0 at the hilt, 1 at the tip. */
/**
 * A blade sequence sampled at a point in the tick, with its bracketing poses.
 *
 * Exported because the chamber test lives on the server — it is a real engine
 * shape cast — but the interpolation is pure geometry and belongs next to the
 * sequence it interpolates. A second copy over there would be a second answer
 * to "where was the blade".
 */
export function bladeAtAlpha(seq, alpha) {
    let i = 1;
    while (i < seq.length - 1 && seq[i].alpha < alpha)
        i += 1;
    const from = seq[i - 1];
    const to = seq[i];
    const span = to.alpha - from.alpha;
    const u = span > 1e-9 ? Math.min(1, Math.max(0, (alpha - from.alpha) / span)) : 0;
    const mix = (p, q) => ({
        x: p.x + (q.x - p.x) * u,
        y: p.y + (q.y - p.y) * u,
        z: p.z + (q.z - p.z) * u,
    });
    return { hilt: mix(from.hilt, to.hilt), tip: mix(from.tip, to.tip), from, to };
}
/**
 * The furthest either endpoint of a blade travels between two points in the tick.
 *
 * Sizes the sub-intervals a clash is cast over. A cast is exact in TRANSLATION
 * but holds the blade's rotation fixed, and a swinging blade rotates — so the
 * interval has to be short enough that the rotation moves the tip less than the
 * blade is thick. Travel is the measure of that, and it is the same rule the
 * body sweep obeys.
 */
export function bladeTravel(seq, lo, hi) {
    const from = bladeAtAlpha(seq, lo);
    const to = bladeAtAlpha(seq, hi);
    return Math.max(Math.hypot(to.hilt.x - from.hilt.x, to.hilt.y - from.hilt.y, to.hilt.z - from.hilt.z), Math.hypot(to.tip.x - from.tip.x, to.tip.y - from.tip.y, to.tip.z - from.tip.z));
}
/** The slice of the tick in which both blades are live, or undefined if none. */
export function bladeOverlap(a, b) {
    if (a.length < 2 || b.length < 2)
        return undefined;
    const lo = Math.max(a[0].alpha, b[0].alpha);
    const hi = Math.min(a[a.length - 1].alpha, b[b.length - 1].alpha);
    return hi < lo ? undefined : { lo, hi };
}
export function bladePoint(pose, s) {
    return {
        x: lerp(pose.hilt.x, pose.tip.x, s),
        y: lerp(pose.hilt.y, pose.tip.y, s),
        z: lerp(pose.hilt.z, pose.tip.z, s),
    };
}
/**
 * Velocity of the point at blade parameter `s` between two consecutive poses,
 * m/s. This is the ONLY way contact velocity is computed, and it reads the same
 * poses the sweeps do.
 */
export function bladePointVelocity(from, to, s) {
    const dt = (to.timeMs - from.timeMs) / 1000;
    if (dt <= 0)
        return { x: 0, y: 0, z: 0 };
    const a = bladePoint(from, s);
    const b = bladePoint(to, s);
    return { x: (b.x - a.x) / dt, y: (b.y - a.y) / dt, z: (b.z - a.z) / dt };
}
// ---------------------------------------------------------------------------
// drawing the weapon: the same data the server hits with
// ---------------------------------------------------------------------------
/**
 * The weapon at rest: literally frame zero of the slash clip.
 *
 * Derived rather than declared. Hand-maintained copies of the rig's rest pose
 * drift the moment anyone edits the rig, and the drift shows up as an idle
 * stance that does not match the first frame of every swing.
 */
export const REST_SWORD = readSample(TRAJECTORIES.slash, 0);
/**
 * How far an elbow must sit from the spine to be clear of the torso, m.
 *
 * A threshold rather than a target: the solver gains nothing by going further
 * out, which is what stops the arms splaying.
 */
const ELBOW_CLEARANCE = 0.235;
/** Length of one arm bone, m. Both are equal, which lets the elbow be exact. */
const ARM_BONE = 0.37;
export const ARM_REACH = ARM_BONE * 2;
/**
 * Solves the elbow for a two-bone arm from `shoulder` to `hand`.
 *
 * With equal bones the elbow lies on a circle about the shoulder-to-hand axis,
 * a distance sqrt(L^2 - (d/2)^2) off it — exact, so both bones always hold their
 * true length. The position ON that circle is searched for the one furthest from
 * the spine, which is what keeps forearms out of the chest without hand-tuned
 * bend directions.
 */
export function bendElbow(shoulder, hand, side) {
    const dx = hand[0] - shoulder[0];
    const dy = hand[1] - shoulder[1];
    const dz = hand[2] - shoulder[2];
    const full = Math.hypot(dx, dy, dz) || 1e-6;
    const reach = Math.min(full, ARM_REACH * 0.999);
    const ux = dx / full;
    const uy = dy / full;
    const uz = dz / full;
    const half = reach / 2;
    const offset = Math.sqrt(Math.max(0, ARM_BONE * ARM_BONE - half * half));
    const mx = shoulder[0] + ux * half;
    const my = shoulder[1] + uy * half;
    const mz = shoulder[2] + uz * half;
    if (offset < 1e-6)
        return [mx, my, mz];
    let ax = -uy;
    let ay = ux;
    let az = 0;
    if (Math.hypot(ax, ay, az) < 1e-6) {
        ax = 0;
        ay = -uz;
        az = uy;
    }
    const al = Math.hypot(ax, ay, az) || 1;
    ax /= al;
    ay /= al;
    az /= al;
    const bx = ay * uz - az * uy;
    const by = az * ux - ax * uz;
    const bz = ax * uy - ay * ux;
    let best = [mx, my, mz];
    let bestScore = -Infinity;
    for (let i = 0; i < 16; i += 1) {
        const t = (i / 16) * Math.PI * 2;
        const c = Math.cos(t);
        const sn = Math.sin(t);
        const ex = mx + (ax * c + bx * sn) * offset;
        const ey = my + (ay * c + by * sn) * offset;
        const ez = mz + (az * c + bz * sn) * offset;
        // Elbows hang BETWEEN the shoulder and the hand, a little below the line
        // joining them. Two earlier rules both failed by chasing an extreme:
        // maximising distance from the spine winged them out at shoulder height,
        // and minimising height dropped them below the hands into a droop. A
        // target height is the thing that actually describes an elbow.
        const target = (shoulder[1] + hand[1]) / 2 - 0.1;
        // Clearing the torso is a THRESHOLD, not something to maximise: past
        // ELBOW_CLEARANCE there is nothing more to gain, so the arm tucks in rather
        // than splaying. The side term is a tie-break that keeps each elbow on its
        // own side of the body.
        const clearance = Math.min(Math.hypot(ex, ez), ELBOW_CLEARANCE);
        const score = -Math.abs(ey - target) * 2.6 + clearance * 1.4 + ex * side * 0.12;
        if (score > bestScore) {
            bestScore = score;
            best = [ex, ey, ez];
        }
    }
    return best;
}
/**
 * How far the chest has turned into the swing.
 *
 * Read off the sword rather than authored: the hands are out to the right, so
 * the chest is turned to the right; the hands are driven forward, so the chest
 * leans after them. That means the torso follows any clip, including ones this
 * file has never seen, without a second animation track to keep in step.
 *
 * Two bounded scalars and nothing else, deliberately. This is the same body
 * that used to be derived from whatever rigid move placed the blade — and ended
 * up lying on the floor. A twist and a lean, each clamped, cannot do that: the
 * worst either can do is turn the shoulders a little too far.
 */
export function torsoPose(hilt, tip) {
    const [px, py, pz] = FEEL.SLASH_PIVOT;
    // The twist follows where the SWORD is, not where the hands are. Read from
    // the hands alone it barely moved: winding up for a side cut gathers them in
    // towards your centre, so the chest stayed square while the blade was drawn
    // right back — which is precisely the pose that should be most turned.
    const lateral = (hilt[0] + tip[0]) / 2 - px;
    // How far in front of the chest the hands are, past where they rest.
    const ahead = pz - hilt[2] - FEEL.SWING_STANDOFF;
    void py;
    const clamp1 = (v) => (v < -1 ? -1 : v > 1 ? 1 : v);
    return {
        // Hands to the character's right (+X) turn the chest to face right, which
        // is a NEGATIVE yaw: +yaw swings forward (-Z) towards -X.
        twist: -clamp1(lateral / 0.55) * FEEL.TORSO_TWIST_MAX,
        lean: clamp1(ahead / 0.5) * FEEL.TORSO_LEAN_MAX,
        // Only forwards: nobody lunges backwards out of a guard.
        lunge: Math.max(0, clamp1(ahead / 0.4)) * FEEL.TORSO_LUNGE_MAX,
        // Fighting never folds the body; emotes set this themselves.
        fold: 0,
    };
}
/**
 * The two shoulders for a given chest attitude.
 *
 * The shoulder line ROTATES about SLASH_PIVOT, which sits exactly between the
 * shoulders, and then carries forward by the lunge. So their midpoint is always
 * the pivot displaced along one axis by one bounded number — the chest turns on
 * the spot and leans, and can do nothing else. That is what keeps the figure on
 * its feet by construction rather than by tuning.
 */
export const shoulderMidFor = (torso) => foldAboutHips([FEEL.SLASH_PIVOT[0], FEEL.SLASH_PIVOT[1], FEEL.SLASH_PIVOT[2] - torso.lunge], torso.fold);
/** Height the upper body pivots about when it bows, m above the feet. */
export const HIP_PIVOT_Y = FEEL.BODY_HEIGHT * 0.5;
/**
 * Folds a point forward about the hips.
 *
 * A bow rotates the whole upper body — shoulders, chest, head and the arms
 * hanging off them — about one point, so everything that fold applies to goes
 * through here. Bending only the parts you remember to bend is how a figure ends
 * up with its head somewhere its neck cannot reach.
 */
export function foldAboutHips(p, fold) {
    if (fold === 0)
        return [p[0], p[1], p[2]];
    const c = Math.cos(fold);
    const sn = Math.sin(fold);
    const dy = p[1] - HIP_PIVOT_Y;
    // Forward is -Z, so folding forward carries height into -Z.
    return [p[0], HIP_PIVOT_Y + dy * c, p[2] - dy * sn];
}
export function shouldersFor(torso) {
    const turn = mulQuat(axisQuat([0, 1, 0], torso.twist), axisQuat([1, 0, 0], torso.lean));
    const carry = (p) => [
        p[0],
        p[1],
        p[2] - torso.lunge,
    ];
    return {
        right: foldAboutHips(carry(rotateAboutPivot(REST_SHOULDER_R, turn)), torso.fold),
        left: foldAboutHips(carry(rotateAboutPivot(REST_SHOULDER_L, turn)), torso.fold),
    };
}
/** Both arms, reaching from their shoulders to their place on the grip. */
function armsHolding(hilt, tip, shoulders) {
    const dx = tip[0] - hilt[0];
    const dy = tip[1] - hilt[1];
    const dz = tip[2] - hilt[2];
    const len = Math.hypot(dx, dy, dz) || 1;
    const onGrip = (back) => [
        hilt[0] - (dx / len) * back,
        hilt[1] - (dy / len) * back,
        hilt[2] - (dz / len) * back,
    ];
    const reachTo = (shoulder, target, side) => {
        // Never stretch: if a swing carries the grip out of range the hand reaches
        // towards it at arm's length instead of the bones growing.
        const rx = target[0] - shoulder[0];
        const ry = target[1] - shoulder[1];
        const rz = target[2] - shoulder[2];
        const d = Math.hypot(rx, ry, rz) || 1;
        const k = Math.min(d, ARM_REACH * 0.98) / d;
        const hand = [
            shoulder[0] + rx * k,
            shoulder[1] + ry * k,
            shoulder[2] + rz * k,
        ];
        return { shoulder, elbow: bendElbow(shoulder, hand, side), hand };
    };
    return {
        arm: reachTo([...shoulders.right], onGrip(FEEL.SWORD_GRIP_LENGTH * 0.2), 1),
        offArm: reachTo([...shoulders.left], onGrip(FEEL.SWORD_GRIP_LENGTH * 0.75), -1),
    };
}
/**
 * THE pose function: where the sword is, and the arms that hold it.
 *
 * The BODY does not appear here at all, and that is the point. Shoulders are
 * fixed constants on an upright torso; only the arms reach for the sword. Every
 * previous version folded the shoulders into whatever rigid move placed the
 * blade, so posing the sword dragged the whole skeleton around and the character
 * ended up lying on the floor. A body does not tip over because its hands moved.
 */
export function localPoseAt(traj, clipTimeMs, angle) {
    // Beyond a quarter turn the arc is mirrored rather than rolled further: past
    // that a roll inverts the grip and the top hand ends up underneath.
    const dial = wrapAngle(angle);
    const mirrored = isMirroredDial(dial);
    const roll = mirrored ? wrapAngle(Math.PI - dial) : dial;
    const sample = rollSample(sampleTrajectoryAt(traj, clipTimeMs), roll);
    const reflected = mirrored
        ? {
            pos: [-sample.pos[0], sample.pos[1], sample.pos[2]],
            rot: [sample.rot[0], -sample.rot[1], -sample.rot[2], sample.rot[3]],
        }
        : sample;
    const capsule = swordCapsuleLocal(reflected);
    const torso = torsoPose(capsule.hilt, capsule.tip);
    const arms = armsHolding(capsule.hilt, capsule.tip, shouldersFor(torso));
    return { hilt: capsule.hilt, tip: capsule.tip, torso, ...arms };
}
/** Where the sword sits for a block stance, and the arms holding it there. */
export function blockPose(_trajectories, pitch, dial = 0) {
    const stance = STANCES[blockStance(dial)];
    const clamped = Math.min(FEEL.PITCH_MAX, Math.max(FEEL.PITCH_MIN, pitch));
    const lift = clamped * FEEL.BLOCK_PITCH_RANGE;
    const hilt = [
        stance.hilt[0],
        stance.hilt[1] + lift,
        stance.hilt[2],
    ];
    const l = Math.hypot(...stance.towards) || 1;
    const tip = [
        hilt[0] + (stance.towards[0] / l) * FEEL.SWORD_LENGTH,
        hilt[1] + (stance.towards[1] / l) * FEEL.SWORD_LENGTH,
        hilt[2] + (stance.towards[2] / l) * FEEL.SWORD_LENGTH,
    ];
    // A guard states its own chest attitude rather than having one derived from
    // where the blade points. Deriving it reads the twist off the sword, which is
    // right for a swing — the body follows the cut — but backwards for a guard:
    // the high block carries the blade off to the RIGHT while the shoulders have
    // to come round to the LEFT to get the hands up past the head. Left to the
    // derivation, the chest turned away from the guard and the arms could not
    // reach it.
    const torso = {
        twist: Math.max(-FEEL.TORSO_TWIST_MAX, Math.min(FEEL.TORSO_TWIST_MAX, stance.twist)),
        lean: 0,
        lunge: 0,
        fold: 0,
    };
    return { hilt, tip, torso, ...armsHolding(hilt, tip, shouldersFor(torso)) };
}
export function blockCapsuleLocal(trajectories, pitch, dial = 0) {
    const pose = blockPose(trajectories, pitch, dial);
    return { hilt: pose.hilt, tip: pose.tip };
}
/**
 * Half the shoulder width, m. Exported because armour is fitted against it:
 * a pauldron that hard-coded the offset it was drawn at would slide off the
 * shoulder the day the figure's proportions changed.
 */
export const SHOULDER_HALF_WIDTH = 0.22;
export const REST_SHOULDER_R = [
    FEEL.SLASH_PIVOT[0] + SHOULDER_HALF_WIDTH,
    FEEL.SLASH_PIVOT[1],
    FEEL.SLASH_PIVOT[2],
];
export const REST_SHOULDER_L = [
    FEEL.SLASH_PIVOT[0] - SHOULDER_HALF_WIDTH,
    FEEL.SLASH_PIVOT[1],
    FEEL.SLASH_PIVOT[2],
];
/**
 * How far an emote folds the body at a point through its run.
 *
 * Only the bow folds, and it eases in and out rather than snapping — the number
 * lives here rather than in the clip because the fold moves the whole upper
 * body, which is the renderer's business, not the blade's.
 */
export function emoteFold(emoteId, t) {
    if (emoteId !== 'bow')
        return 0;
    const u = Math.min(1, Math.max(0, t));
    // Down over the first third, held, up over the last quarter.
    const shape = u < 0.34 ? u / 0.34 : u > 0.76 ? (1 - u) / 0.24 : 1;
    const eased = shape * shape * (3 - 2 * shape);
    return eased * FEEL.BOW_FOLD;
}
/** Folds a whole arm chain about the hips, so nothing is left behind. */
function foldChain(chain, fold) {
    return {
        shoulder: foldAboutHips(chain.shoulder, fold),
        elbow: foldAboutHips(chain.elbow, fold),
        hand: foldAboutHips(chain.hand, fold),
    };
}
export function weaponPose(params) {
    const { animId, t, angle, blocking, pitch, trajectories } = params;
    if (blocking) {
        // While blocking, `angle` carries the defender's dial: it picks the stance.
        return blockPose(trajectories, pitch, params.dial ?? angle);
    }
    // An emote is drawn straight off its own baked clip: same call, same arms,
    // same hands on the grip. The dial does not aim it — an emote is not thrown
    // in a direction — and it folds the body, which nothing in combat does.
    if (isEmoteId(animId)) {
        const traj = trajectories[animId];
        const pose = localPoseAt(traj, t * FEEL.EMOTES[animId].durationMs, 0);
        const fold = emoteFold(animId, t);
        if (fold === 0)
            return pose;
        // A courtesy stays square. Leaving twist/lean derived from the blade made
        // the chest yaw into the sword while the hips folded — a broken spine.
        const torso = animId === 'bow'
            ? {
                ...pose.torso,
                fold,
                twist: pose.torso.twist * 0.12,
                lean: pose.torso.lean * 0.2,
                lunge: pose.torso.lunge * 0.2,
            }
            : { ...pose.torso, fold };
        return {
            ...pose,
            torso,
            hilt: foldAboutHips(pose.hilt, fold),
            tip: foldAboutHips(pose.tip, fold),
            arm: foldChain(pose.arm, fold),
            offArm: foldChain(pose.offArm, fold),
        };
    }
    const isAttack = animId === 'slash' || animId === 'stab';
    const clipId = isAttack ? animId : 'slash';
    const traj = trajectories[clipId];
    const timing = FEEL.CLIPS[clipId];
    const totalMs = timing.windupMs + timing.activeMs + timing.recoveryMs;
    // Not attacking? Frame zero of the slash clip IS the idle stance.
    const elapsedMs = isAttack ? t * totalMs : 0;
    const roll = clipId === 'slash' && isAttack ? angle : 0;
    const target = localPoseAt(traj, elapsedMs, roll);
    const blendMs = params.chainBlendMs ?? 0;
    const from = params.chainFrom;
    // Windup-only: once the blade is live the sweep must read the bake exactly.
    if (!from || blendMs <= 0 || elapsedMs >= timing.windupMs || FEEL.CHAIN_BLEND_MS <= 0) {
        return target;
    }
    // 0 at combo start (still at previous end pose) → 1 when the blend finishes.
    const raw = 1 - Math.min(1, blendMs / FEEL.CHAIN_BLEND_MS);
    const u = raw * raw * (3 - 2 * raw);
    const hilt = [
        from.hilt[0] + (target.hilt[0] - from.hilt[0]) * u,
        from.hilt[1] + (target.hilt[1] - from.hilt[1]) * u,
        from.hilt[2] + (target.hilt[2] - from.hilt[2]) * u,
    ];
    const tip = [
        from.tip[0] + (target.tip[0] - from.tip[0]) * u,
        from.tip[1] + (target.tip[1] - from.tip[1]) * u,
        from.tip[2] + (target.tip[2] - from.tip[2]) * u,
    ];
    const torso = torsoPose(hilt, tip);
    const arms = armsHolding(hilt, tip, shouldersFor(torso));
    return { hilt, tip, torso, ...arms };
}
function capsuleOf(sample, roll) {
    const capsule = swordCapsuleLocal(rollSample(sample, roll));
    return { hilt: capsule.hilt, tip: capsule.tip };
}
const rollPoint = (p, angle) => rollSample({ pos: p, rot: [0, 0, 0, 1] }, angle).pos;
/**
 * Closest points between two segments.
 *
 * Combat uses this rather than the shape-cast's witness points to locate a hit:
 * it needs the blade parameter (tip versus hilt) and the contact height in the
 * same breath, and computing them here keeps them in the one coordinate space
 * everything else uses.
 */
export function closestPointsBetweenSegments(p1, q1, p2, q2) {
    const d1 = { x: q1.x - p1.x, y: q1.y - p1.y, z: q1.z - p1.z };
    const d2 = { x: q2.x - p2.x, y: q2.y - p2.y, z: q2.z - p2.z };
    const r = { x: p1.x - p2.x, y: p1.y - p2.y, z: p1.z - p2.z };
    const a = d1.x * d1.x + d1.y * d1.y + d1.z * d1.z;
    const e = d2.x * d2.x + d2.y * d2.y + d2.z * d2.z;
    const f = d2.x * r.x + d2.y * r.y + d2.z * r.z;
    const c = d1.x * r.x + d1.y * r.y + d1.z * r.z;
    const b = d1.x * d2.x + d1.y * d2.y + d1.z * d2.z;
    const EPS = 1e-9;
    let s = 0;
    let t = 0;
    if (a <= EPS && e <= EPS) {
        // Both degenerate to points.
        s = 0;
        t = 0;
    }
    else if (a <= EPS) {
        s = 0;
        t = clamp01(f / e);
    }
    else if (e <= EPS) {
        t = 0;
        s = clamp01(-c / a);
    }
    else {
        const denom = a * e - b * b;
        s = denom > EPS ? clamp01((b * f - c * e) / denom) : 0;
        t = (b * s + f) / e;
        if (t < 0) {
            t = 0;
            s = clamp01(-c / a);
        }
        else if (t > 1) {
            t = 1;
            s = clamp01((b - c) / a);
        }
    }
    const c1 = { x: p1.x + d1.x * s, y: p1.y + d1.y * s, z: p1.z + d1.z * s };
    const c2 = { x: p2.x + d2.x * t, y: p2.y + d2.y * t, z: p2.z + d2.z * t };
    return {
        s,
        t,
        point: { x: (c1.x + c2.x) / 2, y: (c1.y + c2.y) / 2, z: (c1.z + c2.z) / 2 },
        distance: Math.hypot(c2.x - c1.x, c2.y - c1.y, c2.z - c1.z),
    };
}
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
/** The body capsule's axis segment: the straight part between the two end caps. */
export function bodySegment(centre) {
    const half = FEEL.BODY_HEIGHT / 2 - FEEL.BODY_RADIUS;
    return {
        from: { x: centre.x, y: centre.y - half, z: centre.z },
        to: { x: centre.x, y: centre.y + half, z: centre.z },
    };
}
// ---------------------------------------------------------------------------
// blocking
// ---------------------------------------------------------------------------
/**
 * Where a blocking player's sword physically is, in root space.
 *
 * The guard is aimed with the same dial that aims attacks: it shifts towards the
 * direction the dial points and lies ACROSS that direction, so it stands in the
 * path of a swing arriving from there. Aim up and you have a high guard that
 * stops an overhead; aim up while an attack comes in low and it goes straight
 * under you (AGENTS.md: all collision goes through the engine). Pitch trims the height on top of that.
 *
 * A single fixed bar cannot cover 360 degrees of attack directions, and should
 * not: blocking is a read, not a toggle. This is a real collider that incoming
 * sweeps hit or miss, never a magic shield.
 */
export function blockStance(dial) {
    // Quadrants about the dial, which points at where the attack comes FROM. Up
    // is the roof, down is the low guard, and either side is a wall of steel on
    // that side. Nearest axis wins, so every direction has a guard and the two
    // that matter most — the sides — are not squeezed into slivers.
    const a = wrapAngle(dial);
    if (a >= Math.PI / 4 && a < (3 * Math.PI) / 4)
        return 'top';
    if (a <= -Math.PI / 4 && a > (-3 * Math.PI) / 4)
        return 'bottom';
    return Math.abs(a) < Math.PI / 2 ? 'right' : 'left';
}
/** Rotates a root-space point about SLASH_PIVOT. */
function rotateAboutPivot(p, q) {
    const [px, py, pz] = FEEL.SLASH_PIVOT;
    const r = rotateByQuat([p[0] - px, p[1] - py, p[2] - pz], q);
    return [px + r[0], py + r[1], pz + r[2]];
}
const axisQuat = (axis, radians) => {
    const h = radians / 2;
    const s = Math.sin(h);
    return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(h)];
};
const mulQuat = (a, b) => [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
];
/**
 * The two stances, described by WHERE THE SWORD GOES.
 *
 * Stated as a hilt position and a blade direction, because that is the thing
 * with a right answer — "hands by the left temple, blade flat across the face"
 * is a sentence about the sword, not about Euler angles. The rigid move that
 * gets the resting assembly there is then derived: one rotation taking the rest
 * blade onto the wanted direction, one translation putting the hilt in place.
 *
 * Rigid, so no bone can deform. Earlier attempts either bent the skeleton onto
 * an arbitrary blade (which laid the character on the floor) or guessed at
 * rotations until something looked plausible (which put the guard behind him).
 */
const STANCES = {
    /**
     * A cut from the right: blade stood up out on the sword side, tipped FORWARD
     * toward the opponent rather than planted orthogonal to the floor.
     *
     * The forward cant is the coverage: a pure vertical wall is a thin line in
     * depth, and an incoming cut (or a centreline thrust) ducks past it. Leaning
     * the tip out in front turns the side into a plane the swing has to cross.
     */
    right: {
        hilt: [0.26, 1.28, -0.3],
        // Strong forward cant: tip well out in front so the side is a plane, not a post.
        towards: [0.18, 0.48, -0.86],
        twist: -0.28,
    },
    /** A cut from the left: the mirror of it, out over the other shoulder. */
    left: {
        hilt: [-0.26, 1.28, -0.3],
        towards: [-0.18, 0.48, -0.86],
        twist: 0.28,
    },
    /**
     * A cut from above: hands up past the RIGHT of the head, blade HORIZONTAL
     * across the face pointing left. The arms cross to do it — the off hand grips
     * below the guard, which on a leftward blade puts it further right than the
     * sword hand.
     *
     * The roof is the only guard with the hands above the shoulders, and that
     * crossed-over off arm is the longest reach any pose in the game asks for. It
     * is why the chest turns hard right here: without the shoulders coming round,
     * the left hand simply cannot get there and the solver slides it off the grip.
     */
    top: {
        hilt: [0.24, 1.68, -0.4],
        towards: [-1, 0.05, -0.04],
        twist: -0.52,
    },
    /**
     * A cut from below: cross-body low. Hands at the right hip, blade laid across
     * the pelvis with a mild tip-down / tip-forward cant — enough slope to read
     * as a real low guard, still on the rising-cut path where steel has to be.
     */
    bottom: {
        // Slightly inboard of a full right-hip hold so looking down stays in reach.
        hilt: [0.1, 1.15, -0.42],
        towards: [-0.95, -0.22, -0.22],
        twist: 0.16,
    },
};
/** Shortest-arc rotation taking unit vector `a` onto unit vector `b`. */
function rotationBetween(a, b) {
    const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    if (dot > 0.999999)
        return [0, 0, 0, 1];
    if (dot < -0.999999) {
        // Opposed: any perpendicular axis will do for a half turn.
        const axis = Math.abs(a[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
        const px = a[1] * axis[2] - a[2] * axis[1];
        const py = a[2] * axis[0] - a[0] * axis[2];
        const pz = a[0] * axis[1] - a[1] * axis[0];
        const pl = Math.hypot(px, py, pz) || 1;
        return [px / pl, py / pl, pz / pl, 0];
    }
    const cx = a[1] * b[2] - a[2] * b[1];
    const cy = a[2] * b[0] - a[0] * b[2];
    const cz = a[0] * b[1] - a[1] * b[0];
    const w = 1 + dot;
    const n = Math.hypot(cx, cy, cz, w) || 1;
    return [cx / n, cy / n, cz / n, w / n];
}
/** True while a block is still inside its parry window. */
export function inParryWindow(blockElapsedMs) {
    return blockElapsedMs >= 0 && blockElapsedMs < FEEL.PARRY_WINDOW_MS;
}
// ---------------------------------------------------------------------------
// damage
// ---------------------------------------------------------------------------
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
/**
 * Which zone a contact height lands in. `feetY` is the bottom of the body
 * capsule.
 *
 * Ordered narrowest-first: the foot band sits INSIDE the leg band, so testing
 * legs first would swallow it and the boots slot would never be struck.
 */
export function hitZone(contactY, feetY) {
    const fraction = (contactY - feetY) / FEEL.BODY_HEIGHT;
    if (fraction >= 1 - FEEL.HEAD_ZONE_FRACTION)
        return 'head';
    if (fraction <= FEEL.FOOT_ZONE_FRACTION)
        return 'feet';
    if (fraction <= FEEL.LEG_ZONE_FRACTION)
        return 'legs';
    return 'torso';
}
/** Damage multiplier for where a hit landed. The torso is the 1.0 reference. */
export function zoneMultiplier(zone) {
    switch (zone) {
        case 'head':
            return FEEL.HEAD_MULT;
        case 'legs':
            return FEEL.LEG_MULT;
        case 'feet':
            return FEEL.FOOT_MULT;
        case 'arm':
            return FEEL.ARM_MULT;
        case 'torso':
            return 1;
    }
}
/**
 * Damage for one connection: the swing's base, scaled by where it landed, less
 * whatever the target was wearing there.
 *
 * The order matters and is not arbitrary. The zone multiplier is a property of
 * the TARGET'S ANATOMY — a head is a worse place to be hit than a foot whatever
 * you are wearing — so it applies first, and armour then takes its fraction of
 * the result. Subtracting armour first would let boots blunt a headshot.
 *
 * Speed is deliberately not in here. It is still measured and still travels in
 * the combat event, where it drives hitstop and screen shake, but it stopped
 * scaling damage when spinning into a swing turned out to be worth more than
 * aiming one.
 */
export function computeDamage(clipId, zone, target) {
    const raw = FEEL.CLIPS[clipId].baseDamage * zoneMultiplier(zone);
    return raw * (1 - armorAt(target, zone));
}
/** Relative velocity of a blade contact point against a moving target. */
export function relativeVelocity(contactVel, targetVel) {
    return {
        x: contactVel.x - targetVel.x,
        y: contactVel.y - targetVel.y,
        z: contactVel.z - targetVel.z,
    };
}
/**
 * The speed that feeds the damage curve: the component of the relative velocity
 * pointing INTO the target. A blade sliding past at speed does not hit hard.
 */
export function closingSpeed(relVel, contactNormal) {
    const len = Math.hypot(contactNormal.x, contactNormal.y, contactNormal.z);
    if (len < 1e-9)
        return Math.hypot(relVel.x, relVel.y, relVel.z);
    const nx = contactNormal.x / len;
    const ny = contactNormal.y / len;
    const nz = contactNormal.z / len;
    return Math.abs(relVel.x * nx + relVel.y * ny + relVel.z * nz);
}
//# sourceMappingURL=combat.js.map